import { useState, useEffect } from 'react';
import { Navbar } from '@/components/layout/Navbar';
import { supabase } from '@/lib/supabase';
import { useNavigate, Link } from 'react-router-dom';
import { BookOpen, Cpu, Key, Zap } from 'lucide-react';

export function DocsIndex() {
  const [user, setUser] = useState<any>(null);
  const navigate = useNavigate();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  const handleSearch = (query: string) => {
    if (query?.trim()) {
      navigate(`/saved?query=${encodeURIComponent(query.trim())}`);
      return;
    }
    navigate('/saved');
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar user={user} onSearch={handleSearch} onSignOut={handleSignOut} />
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <h1 className="text-4xl font-bold mb-2">Documentation</h1>
        <p className="text-muted-foreground text-lg mb-10">
          Learn how to connect AI agents to your Solem knowledge base.
        </p>

        <div className="grid gap-6 sm:grid-cols-2">
          <Link
            to="/docs/mcp"
            className="group rounded-xl border bg-card p-6 hover:border-primary/50 hover:shadow-md transition-all"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="rounded-lg bg-primary/10 p-2">
                <Cpu className="h-5 w-5 text-primary" />
              </div>
              <h2 className="text-xl font-semibold group-hover:text-primary transition-colors">
                MCP Server
              </h2>
            </div>
            <p className="text-muted-foreground text-sm">
              Connect Claude, Cursor, OpenClaw, and other AI agents to your saved pages via the Model Context Protocol.
            </p>
          </Link>

          <Link
            to="/docs/mcp#getting-your-api-key"
            className="group rounded-xl border bg-card p-6 hover:border-primary/50 hover:shadow-md transition-all"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="rounded-lg bg-primary/10 p-2">
                <Key className="h-5 w-5 text-primary" />
              </div>
              <h2 className="text-xl font-semibold group-hover:text-primary transition-colors">
                API Keys
              </h2>
            </div>
            <p className="text-muted-foreground text-sm">
              Create and manage API keys for authenticating your AI agents.
            </p>
          </Link>

          <Link
            to="/docs/mcp#tools-reference"
            className="group rounded-xl border bg-card p-6 hover:border-primary/50 hover:shadow-md transition-all"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="rounded-lg bg-primary/10 p-2">
                <BookOpen className="h-5 w-5 text-primary" />
              </div>
              <h2 className="text-xl font-semibold group-hover:text-primary transition-colors">
                Tools Reference
              </h2>
            </div>
            <p className="text-muted-foreground text-sm">
              Full reference for search, save, get, update, delete, and list_recent tools.
            </p>
          </Link>

          <Link
            to="/docs/mcp#workflows--best-practices"
            className="group rounded-xl border bg-card p-6 hover:border-primary/50 hover:shadow-md transition-all"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="rounded-lg bg-primary/10 p-2">
                <Zap className="h-5 w-5 text-primary" />
              </div>
              <h2 className="text-xl font-semibold group-hover:text-primary transition-colors">
                Best Practices
              </h2>
            </div>
            <p className="text-muted-foreground text-sm">
              Recommended workflows and agent guidelines for working with saved content.
            </p>
          </Link>
        </div>
      </div>
    </div>
  );
}
